import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import muzukuruLogo from "../../../assets/muzukurublack.png";
import ctaImage1 from "../../../assets/cta-pattern.png";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import apiService, { BaseURL } from "../../../service/Api";
import { showSuccessToast } from "../../../components/common/ShowToast";
import CtaSlideshow from "./CTASlideshow";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const uidb64 = searchParams.get("uidb64");
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const ctaImages = [ctaImage1, ctaImage2];

  if (!uidb64 || !token) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          Invalid reset link
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The password reset link is invalid or has expired.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/forgot-password")}
          sx={{
            mt: 2,
            backgroundColor: "#6b4c3b",
            "&:hover": { backgroundColor: "#55392d" },
          }}
        >
          Request New Reset Link
        </Button>
      </Box>
    );
  }

  const showErrorToast = (message: string, toastId: string = "error-toast") => {
    toast.dismiss(toastId);
    toast.error(message, {
      toastId,
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      style: {
        backgroundColor: "#fff",
        color: "#333",
        border: "1px solid #C29363",
        zIndex: 9999,
      },
    });
  };

  const validatePassword = (password: string): string | null => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long.";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!hasNumber) {
      return "Password must contain at least one number.";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character.";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!newPassword || !confirmPassword) {
      showErrorToast("Please enter both password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      showErrorToast("Passwords do not match.");
      return;
    }

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      showErrorToast(passwordValidationError);
      return;
    }

    setIsLoading(true);
    try {
      await apiService.post(
        `/api/authentication/reset-password/${uidb64}/${token}/`,
        { new_password: newPassword, confirm_password: confirmPassword },
        true,
      );
      setIsPasswordReset(true);
      showSuccessToast("Password reset successfully. Redirecting to login...");
      setTimeout(() => navigate("/login"), 5000);
    } catch (err: unknown) {
      let errorMessage = "An error occurred. Please try again.";
      const errorResponse = (
        err as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      )?.response;
      if (errorResponse?.data?.message) {
        errorMessage = errorResponse.data.message;
      } else if (
        (err as { message?: string })?.message?.includes("Network Error")
      ) {
        errorMessage = "Network error. Please check your connection.";
      }
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Animation variants ---- //
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };
  const successVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fff",
      height: "52px",
      "& fieldset": { borderColor: "#d4d4d4" },
      "&:hover fieldset": { borderColor: "#a3a3a3" },
      "&.Mui-focused fieldset": { borderColor: "#6b4c3b" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a", fontSize: "15px" },
  };

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        position: "relative",
        border: isStaging ? "10px solid #FFBB00" : "none",
        boxSizing: "border-box",
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: "#fff",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
          }}
        >
          {/* STAGING badge */}
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box
              component="img"
              src={muzukuruLogo}
              alt="Muzukuru Funeral Streaming"
              sx={{ height: 48, objectFit: "contain" }}
            />
          </Box>

          {/* Form content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {" "}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 420, width: "100%" }}
            >
              {!isPasswordReset ? (
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Back link */}
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        color: "#6b6b6b",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "14px",
                        marginBottom: "16px",
                        fontWeight: 500,
                      }}
                    >
                      {"<- Back to Sign In"}
                    </Link>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        mb: 0.5,
                        fontSize: { xs: "24px", md: "28px" },
                      }}
                    >
                      Set New Password
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        color: "#6b6b6b",
                        fontSize: "15px",
                        mb: 4,
                        lineHeight: 1.6,
                      }}
                    >
                      {
                        "Enter a new password for your account. Ensure it's strong and secure."
                      }
                    </Typography>
                  </motion.div>

                  {/* New Password */}
                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontSize: "14px",
                        mb: 1,
                      }}
                    >
                      New Password
                    </Typography>
                    <TextField
                      placeholder="Enter your new password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isLoading}
                      error={!!passwordError}
                      helperText={passwordError}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={isLoading}
                              sx={{ color: "#999" }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...fieldSx, mb: 3 }}
                    />
                  </motion.div>

                  {/* Confirm Password */}
                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontSize: "14px",
                        mb: 1,
                      }}
                    >
                      Confirm Password
                    </Typography>
                    <TextField
                      placeholder="Confirm your new password"
                      variant="outlined"
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              disabled={isLoading}
                              sx={{ color: "#999" }}
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...fieldSx, mb: 3.5 }}
                    />
                  </motion.div>

                  {/* Submit button */}
                  <motion.div variants={itemVariants}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        backgroundColor: "#6b4c3b",
                        textTransform: "none",
                        padding: "14px",
                        borderRadius: "8px",
                        fontSize: "16px",
                        fontWeight: 600,
                        boxShadow: "none",
                        color: "#fff",
                        mb: 3,
                        "&:hover": {
                          backgroundColor: "#55392d",
                          boxShadow: "none",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(107,76,59,0.4)",
                          color: "#fff",
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} sx={{ color: "#fff" }} />
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      align="center"
                      sx={{ color: "#6b6b6b", fontSize: "14px" }}
                    >
                      {"Remember your password? "}
                      <Link
                        to="/login"
                        style={{
                          color: "#6b4c3b",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Sign in
                      </Link>
                    </Typography>
                  </motion.div>
                </Box>
              ) : (
                /* ---- Success State ---- */
                <motion.div
                  variants={successVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ textAlign: "center" }}
                >
                  <Box sx={{ mb: 3 }}>
                    <CheckCircle sx={{ fontSize: "72px", color: "#6b4c3b" }} />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#1a1a1a", mb: 2 }}
                  >
                    Password Reset Successful
                  </Typography>

                  <Typography
                    sx={{
                      color: "#6b6b6b",
                      mb: 4,
                      fontSize: "15px",
                      lineHeight: 1.6,
                    }}
                  >
                    Your password has been updated. You can now sign in with
                    your new password.
                  </Typography>

                  <Link
                    to="/login"
                    style={{
                      color: "#6b4c3b",
                      fontWeight: 600,
                      textDecoration: "none",
                      fontSize: "15px",
                    }}
                  >
                    {"<- Back to Sign In"}
                  </Link>
                </motion.div>
              )}
            </motion.div>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: "#fff",
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
